import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import type { MutableRefObject, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ChatSettingsPanel } from './chat-settings-panel';
import { CreateSkillView } from './custom-skills-ui';
import { NovaScheduleDetailView, NovaSchedulesSettings } from './nova-schedules-settings';
import { OutlookManageView } from './outlook-connect-settings';
import { SettingsPanelContext, type SettingsPanelCloseGuard, type SettingsPanelView } from './settings-panel-context';
import { NOVA_SCHEDULES_FEATURE_FLAG } from '../../../lib/nova-schedule-proposal';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { SkillsProvider } from '../contexts/skills-context';

interface ChatSettingsContentProps {
  children: ReactNode;
  /** Allows embedded clients to preserve their own General settings while sharing the Skills experience. */
  settingsPanel?: ReactNode;
}

/**
 * Switches a manually composed chat between its normal content and settings.
 * It also owns the shared create/edit/manage subviews used by settings pages.
 */
export function ChatSettingsContent({ children, settingsPanel }: ChatSettingsContentProps) {
  const {
    isSettingsOpen,
    setIsSettingsOpen,
    settingsBackSignal,
    settingsCloseIntentRef,
    setHasActiveSettingsSubView,
    settingsSection,
  } = useChatOverlayContext();
  const [activeView, setActiveView] = useState<SettingsPanelView | null>(null);
  const areNovaSchedulesEnabled = useFeatureFlag(NOVA_SCHEDULES_FEATURE_FLAG, false);
  const backHandlerRef = useRef<SettingsPanelCloseGuard | null>(null);
  const initialSettingsBackSignalRef = useRef(settingsBackSignal);

  const closeSettingsSubView = useCallback(
    (afterClose?: () => void) => {
      backHandlerRef.current = null;
      setActiveView(null);
      setHasActiveSettingsSubView(false);

      if (afterClose) {
        setIsSettingsOpen(false);
        afterClose();
      }
    },
    [setHasActiveSettingsSubView, setIsSettingsOpen],
  );

  useEffect(() => {
    return () => {
      backHandlerRef.current = null;
      setHasActiveSettingsSubView(false);
    };
  }, [setHasActiveSettingsSubView]);

  useEffect(() => {
    if (settingsBackSignal === initialSettingsBackSignalRef.current) return;
    initialSettingsBackSignalRef.current = settingsBackSignal;

    const afterClose = settingsCloseIntentRef.current ?? undefined;
    settingsCloseIntentRef.current = null;
    const backHandler = backHandlerRef.current;

    if (activeView || backHandler) {
      if (backHandler) {
        void Promise.resolve(backHandler()).then((canClose) => {
          if (canClose !== false) {
            if (activeView) {
              closeSettingsSubView(afterClose);
            } else {
              setIsSettingsOpen(false);
              afterClose?.();
            }
          }
        });
      } else {
        closeSettingsSubView(afterClose);
      }
      return;
    }

    if (isSettingsOpen) {
      setIsSettingsOpen(false);
      afterClose?.();
    }
  }, [activeView, closeSettingsSubView, isSettingsOpen, setIsSettingsOpen, settingsBackSignal, settingsCloseIntentRef]);

  const contextValue = useMemo(
    () => ({
      pushView: (view: SettingsPanelView) => {
        backHandlerRef.current = null;
        setActiveView(view);
        setHasActiveSettingsSubView(true);
      },
      popView: () => closeSettingsSubView(),
      backHandlerRef,
    }),
    [closeSettingsSubView, setHasActiveSettingsSubView],
  );

  return (
    <SkillsProvider>
      <SettingsPanelContext.Provider value={contextValue}>
        {activeView ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <SettingsSubView view={activeView} onBack={() => closeSettingsSubView()} cancelRef={backHandlerRef} />
          </div>
        ) : isSettingsOpen ? (
          (settingsPanel ?? <ChatSettingsPanel />)
        ) : settingsSection === 'scheduled' && areNovaSchedulesEnabled ? (
          <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto px-8 pt-10 pb-8 [scrollbar-gutter:stable_both-edges]">
            <div className="flex w-[736px] max-w-full shrink-0 flex-col gap-4">
              <NovaSchedulesSettings />
            </div>
          </div>
        ) : (
          children
        )}
      </SettingsPanelContext.Provider>
    </SkillsProvider>
  );
}

function SettingsSubView({
  view,
  onBack,
  cancelRef,
}: {
  view: SettingsPanelView;
  onBack: () => void;
  cancelRef: MutableRefObject<SettingsPanelCloseGuard | null>;
}) {
  switch (view.type) {
    case 'outlook-manage':
      return <OutlookManageView onBack={onBack} />;
    case 'nova-schedule-detail':
      return <NovaScheduleDetailView scheduleUuid={view.scheduleUuid} onBack={onBack} />;
    case 'custom-skill-create':
      return <CreateSkillView onBack={onBack} cancelRef={cancelRef} />;
    case 'custom-skill-edit':
      return <CreateSkillView onBack={onBack} skillToUpdate={view.skill} cancelRef={cancelRef} />;
  }
}
